import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  convertToPixelCrop,
} from "react-image-crop";
import "react-image-crop/src/ReactCrop.scss";
import { canvasPreview, ensureMinimumCanvasSize } from "./canvas-preview";
import "./ImageCropper.css";

export const CROP_ASPECT_OPTIONS = [
  { id: "free", label: "Free", aspect: undefined },
  { id: "square", label: "Square", aspect: 1 },
  { id: "landscape", label: "Landscape", aspect: 4 / 3 },
  { id: "wide", label: "Wide", aspect: 16 / 9 },
];

const PROFILE_ASPECT = 1;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

const centerAspectCrop = (mediaWidth, mediaHeight, aspect) =>
  centerCrop(
    makeAspectCrop({ unit: "%", width: 90 }, aspect, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight
  );

const buildFreeCrop = () => ({
  unit: "%",
  x: 5,
  y: 5,
  width: 90,
  height: 90,
});

const hasSavedCrop = (savedCrop) =>
  savedCrop && savedCrop.width && savedCrop.height && savedCrop.unit;

const buildInitialCrop = (savedCrop, mediaWidth, mediaHeight, aspect) => {
  if (hasSavedCrop(savedCrop)) {
    const crop = {
      unit: savedCrop.unit,
      x: Number(savedCrop.x) || 0,
      y: Number(savedCrop.y) || 0,
      width: Number(savedCrop.width),
      height: Number(savedCrop.height),
    };

    if (crop.unit === "%") {
      return crop;
    }

    return convertToPixelCrop(crop, mediaWidth, mediaHeight);
  }

  if (aspect) {
    return centerAspectCrop(mediaWidth, mediaHeight, aspect);
  }

  return buildFreeCrop();
};

const ImageCropper = ({
  imgSrc,
  savedCrop,
  onChange,
  defaultAspectId = "free",
  variant = "default",
}) => {
  const isProfile = variant === "profile";
  const [aspectOption, setAspectOption] = useState(
    isProfile ? "square" : defaultAspectId
  );
  const [scale, setScale] = useState(1);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [previewUrl, setPreviewUrl] = useState(null);
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const onChangeRef = useRef(onChange);

  const selectedAspect = isProfile
    ? PROFILE_ASPECT
    : CROP_ASPECT_OPTIONS.find((option) => option.id === aspectOption)?.aspect;

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setCrop(undefined);
    setCompletedCrop(undefined);
    setPreviewUrl(null);
    setScale(1);
  }, [imgSrc]);

  const applyCropForAspect = useCallback((aspectId, imageEl) => {
    const aspectValue = CROP_ASPECT_OPTIONS.find(
      (option) => option.id === aspectId
    )?.aspect;

    if (!imageEl) {
      return;
    }

    const { width, height } = imageEl;
    const nextCrop = buildInitialCrop(null, width, height, aspectValue);
    setCrop(nextCrop);
    setCompletedCrop(nextCrop);
  }, []);

  const onImageLoad = useCallback(
    (e) => {
      const { width, height } = e.currentTarget;
      const initialCrop = buildInitialCrop(
        savedCrop,
        width,
        height,
        selectedAspect
      );
      setCrop(initialCrop);
      setCompletedCrop(initialCrop);
    },
    [savedCrop, imgSrc, selectedAspect]
  );

  const handleAspectChange = (aspectId) => {
    setAspectOption(aspectId);
    applyCropForAspect(aspectId, imgRef.current);
  };

  useEffect(() => {
    if (
      !completedCrop?.width ||
      !completedCrop?.height ||
      !imgRef.current ||
      !previewCanvasRef.current
    ) {
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        await canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
          0
        );

        if (cancelled || !previewCanvasRef.current) {
          return;
        }

        const exportCanvas = isProfile
          ? ensureMinimumCanvasSize(previewCanvasRef.current)
          : previewCanvasRef.current;
        const dataUrl = exportCanvas.toDataURL("image/png");
        setPreviewUrl(dataUrl);
        onChangeRef.current?.(completedCrop, dataUrl);
      } catch (error) {
        console.error("Failed to generate crop preview", error);
      }
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [completedCrop, imgSrc, scale]);

  if (!imgSrc) {
    return null;
  }

  if (isProfile) {
    return (
      <div className="profile-image-cropper profile-image-cropper--profile">
        <div className="profile-image-cropper__editor profile-image-cropper__editor--circular">
          <ReactCrop
            crop={crop}
            onChange={(pixelCrop) => setCrop(pixelCrop)}
            onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
            aspect={PROFILE_ASPECT}
            circularCrop
            keepSelection
          >
            <img
              ref={imgRef}
              alt="Adjust image crop"
              src={imgSrc}
              onLoad={onImageLoad}
              className="profile-image-cropper__source"
              style={{ transform: `scale(${scale})` }}
            />
          </ReactCrop>
        </div>
        <div className="profile-image-cropper__zoom">
          <span className="profile-image-cropper__zoom-label">Zoom</span>
          <input
            type="range"
            className="profile-image-cropper__zoom-slider"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={scale}
            onChange={(event) => setScale(Number(event.target.value))}
            aria-label="Zoom image"
          />
        </div>
        <canvas ref={previewCanvasRef} className="profile-image-cropper__canvas" />
      </div>
    );
  }

  return (
    <div className="profile-image-cropper">
      <div className="profile-image-cropper__main">
        <div className="profile-image-cropper__aspect-bar">
          <span className="profile-image-cropper__aspect-label">Crop shape</span>
          <div className="profile-image-cropper__aspect-options">
            {CROP_ASPECT_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`profile-image-cropper__aspect-btn${
                  aspectOption === option.id
                    ? " profile-image-cropper__aspect-btn--active"
                    : ""
                }`}
                onClick={() => handleAspectChange(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="profile-image-cropper__editor">
          <ReactCrop
            crop={crop}
            onChange={(pixelCrop) => setCrop(pixelCrop)}
            onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
            aspect={selectedAspect}
            keepSelection
          >
            <img
              ref={imgRef}
              alt="Adjust image crop"
              src={imgSrc}
              onLoad={onImageLoad}
              className="profile-image-cropper__source"
            />
          </ReactCrop>
        </div>
      </div>

      <div className="profile-image-cropper__preview">
        <p className="profile-image-cropper__preview-label">Preview</p>
        <div className="profile-image-cropper__preview-frame">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Crop preview"
              className="profile-image-cropper__preview-img"
            />
          ) : (
            <div className="profile-image-cropper__preview-placeholder" />
          )}
        </div>
        <p className="profile-image-cropper__hint">
          Drag the corners to crop. Use Square for avatars or Wide for logos.
        </p>
        <canvas ref={previewCanvasRef} className="profile-image-cropper__canvas" />
      </div>
    </div>
  );
};

export default ImageCropper;
